import DeviceControls from 'components/DeviceControls/DeviceControls';
import DeviceHistory from 'components/DeviceHistory/DeviceHistory';
import BaseModal from 'components/Modals/BaseModal';
import BaseTooltip from 'components/Tooltips/BaseTooltip';
import TextToggle from 'components/UI/TextToggle';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import AudioManager from 'features/Integrations/features/ConnectedDevices/features/AudioManager/AudioManager';
import {
  GROUP_HISTORY_PAGE_SIZE,
  GROUP_HISTORY_START_MS,
} from 'features/Integrations/features/ConnectedDeviceGroups/features/GroupSettingsModal/utils/groupSettingsModalUtils';
import DeviceMap from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { getLocationCoordinates } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/utils/DeviceMapUtils';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import VideoPlayer from 'features/VMS/features/VideoPlayer/index.web';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Collapse } from 'react-bootstrap';
import { FaChevronDown } from 'react-icons/fa6';
import { PiPencilLineFill } from 'react-icons/pi';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useKillswitchDeviceMutation } from 'services/devices/devices.api';
import {
  clampPanelHeight,
  DragResizeHandle,
  readStoredPanelHeight,
  storePanelHeight,
} from 'utils/layout/panelResize.utils';
import { notifyErrorToast } from 'utils/notifications/toast.utils';

/** The media (map / video) panel's height is the operator's to set and is remembered. */
const MEDIA_PANEL_HEIGHT_KEY = 'deviceSettings.mediaPanelHeight';
const MEDIA_PANEL_DEFAULT_PX = 360;
const MEDIA_PANEL_DEFAULT_TALL_PX = 480;

export default function DeviceSettings({ device, onSelectDevice }) {
  const [killswitchDevice, { isLoading: isKilling }] = useKillswitchDeviceMutation();

  // A stable reference so AudioManager doesn't re-derive its audio list (and
  // refetch every file) on every unrelated re-render, e.g. a resize.
  const audioManagerDeviceIds = useMemo(
    () => (device?.id != null ? [device.id] : []),
    [device?.id],
  );

  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      useRenderTracker('DeviceSettings');
    }
  } catch (e) {}
  const perms = usePerms();

  const [showVideo, setShowVideo] = useState(false);
  const [displayDevice, setDisplayDevice] = useState(device);
  const [initialized, setInitialized] = useState(false);
  const [sending, setSending] = useState(false);
  const [audioManager, setAudioManager] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);
  // Closed until asked for: with the panel open by default the media view was
  // pushed up and the modal read as a history table (Mike, 2026-08-28).
  const [historyOpen, setHistoryOpen] = useState(false);
  const [stopLiveAudioSignal, setStopLiveAudioSignal] = useState(0);

  // The page load holds ONE row for each device, so this panel would list a single
  // activation. The rest is asked for when the panel is opened, a page at a time;
  // the points merge into the device cache the table below reads. The window starts
  // at the epoch because this panel only exists for command devices, which write a
  // row when a person commands them — see GROUP_HISTORY_START_MS.
  const historyIds = useMemo(() => (device?.id != null ? [device.id] : []), [device?.id]);
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: historyIds,
    startTime: GROUP_HISTORY_START_MS,
    limit: GROUP_HISTORY_PAGE_SIZE,
    enabled: historyOpen,
  });

  const computeMaintenanceState = (targetDevice) =>
    Boolean(targetDevice?.kill_switch) || targetDevice?.data?.[0]?.status === -1;

  const computeDisplayStatus = (targetDevice) => Number(targetDevice?.data?.[0]?.status ?? 0);

  const [maintenanceActive, setMaintenanceActive] = useState(
    computeMaintenanceState(displayDevice),
  );
  const previousStatusRef = useRef(
    computeDisplayStatus(displayDevice) === -1 ? 0 : computeDisplayStatus(displayDevice),
  );

  const createEditPermission = () => {
    const permissionDevice = displayDevice || device;
    const systemType = `${permissionDevice?.system_type || ''}`.replace(/\s/g, '');
    const deviceType = `${permissionDevice?.device_type || ''}`
      .replace(/\s/g, '')
      .replace(/\//g, '-');

    if (!systemType || !deviceType) return false;

    // Sending a command is its own permission (2026-08-28): SendDeviceCommands, not the
    // Create-Edit<Type>Connections key that edits the device. The backend checks the same.
    return perms.testPermission([], ['SendDeviceCommands']);
  };

  useEffect(() => {
    //I think we only need to check initialized because react calls useEffects twice in debug/developer mode
    if (initialized) setDisplayDevice(device);
  }, [device]);

  useEffect(() => {
    if (!displayDevice) return;

    const currentStatus = computeDisplayStatus(displayDevice);
    if (currentStatus !== -1) previousStatusRef.current = currentStatus;
    setMaintenanceActive(computeMaintenanceState(displayDevice));
  }, [displayDevice]);

  const applyMaintenanceToDisplayState = (active) => {
    setDisplayDevice((prev) => {
      if (!prev) return prev;

      const updatedStatus = active ? -1 : (previousStatusRef.current ?? 0);
      const existingData = Array.isArray(prev.data) ? prev.data : [];
      const [firstEntry, ...restEntries] = existingData;

      return {
        ...prev,
        kill_switch: active ? 1 : 0,
        data: [
          {
            ...(firstEntry ?? {}),
            status: updatedStatus,
          },
          ...restEntries,
        ],
      };
    });
  };

  const onEnableMaintenanceMode = async () => {
    if (!perms.isMaintenanceOperator()) return;

    const optimisticState = !maintenanceActive;
    applyMaintenanceToDisplayState(optimisticState);
    setMaintenanceActive(optimisticState);

    try {
      await killswitchDevice({ id: displayDevice.id, state: optimisticState ? 1 : 0 }).unwrap();

      if (!optimisticState) {
        // Leaving maintenance; ensure previous status reflects current display
        const currentStatus = computeDisplayStatus(displayDevice);
        if (currentStatus !== -1) previousStatusRef.current = currentStatus;
      }
    } catch (err) {
      console.error('Error sending device command:', err.message);
      notifyErrorToast('maintenance mode', { isAddOperation: false });

      // Revert optimistic update
      const revertState = !optimisticState;
      applyMaintenanceToDisplayState(revertState);
      setMaintenanceActive(revertState);
    }
  };

  const deviceControlListener = async (evt, data) => {
    switch (evt) {
      case 'update':
        setDisplayDevice(data);
        setInitialized(true);
        return;
      case 'submit':
        setSending(true);
        try {
          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments, //does not exist in data
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments || 'No Comment Provided',
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
        } catch (error) {
          console.error('Error sending device command:', error);
        }
        setSending(false);
        return;
      case 'cancel-audio':
        try {
          await sendDeviceCommand(
            {
              id: device.id,
              node_id: device.node_id,
              system_type: device.system_type,
              device_type: device.device_type,
            },
            7,
            '',
            false,
          );
        } catch (err) {
          console.error('Error sending device command:', err.message);
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const onSetTempAudio = (fileName) => {
    setAudioOverride({
      id: -1,
      name: fileName,
      displayName: fileName,
      relay_id: -1,
      device_ids: null,
    });

    setAudioManager(false);
  };

  const deviceMapBounds = useMemo(() => {
    const coords = getLocationCoordinates(displayDevice?.location);
    if (!coords) return null;
    return [coords, coords];
  }, [displayDevice?.location]);

  const displayDeviceType = displayDevice?.device_type?.toLowerCase?.() || '';
  const hasMediaPreview = device.device_type?.toLowerCase() === 'overwatch vms' || device?.camera;
  /**
   * A demo siren must LOOK like an integrated one — the Video toggle and the
   * Listen Live control stay where they are — but must never open a stream or
   * claim to be playing one. Both are greyed out and one line says why. Mike,
   * 2026-08-28: "display a message saying vms is unavailable for this device,
   * along with live audio, it should be greyed out and unavailable".
   *
   * The word is DEMO, never "simulated" and never against "real" (Mike,
   * 2026-09-01): a deployment is all real or all demo, so the line says what
   * this device cannot do, not what kind of fleet it belongs to.
   */
  const isDemoDevice = Number(device?.is_simulated) === 1;
  const hasDeviceLayout = Boolean(layout[displayDeviceType]);
  const isMediaOnlySettings = hasMediaPreview && !hasDeviceLayout;
  const videoDeviceId = device.camera ? device.camera : device.id;
  const videoFeedUrl = `${process.env.PUBLIC_API_URL}/v2/integrations/device/camera/${encodeURIComponent(videoDeviceId)}/video-stream/index.m3u8`;
  const videoPlayerLabel = `${device?.alias || device?.name || device?.id || 'Device'} camera feed`;
  // The media panel is drag-resizable (the handle under it) and its height is
  // remembered across sessions. It used to be a fixed 260 px on a device with
  // controls — "not tall enough to see what's being displayed" once a siren has
  // a camera feed (Mike, 2026-08-28). BaseMap's MapInvalidateSizeFix watches the
  // container with a ResizeObserver, so the map redraws as it grows.
  const mediaPanelDefaultPx = isMediaOnlySettings
    ? MEDIA_PANEL_DEFAULT_TALL_PX
    : MEDIA_PANEL_DEFAULT_PX;
  const [mediaPanelHeight, setMediaPanelHeight] = useState(() =>
    clampPanelHeight(readStoredPanelHeight(MEDIA_PANEL_HEIGHT_KEY, mediaPanelDefaultPx)),
  );
  const resizeMediaPanel = (wantedPx) => {
    const next = clampPanelHeight(wantedPx);
    setMediaPanelHeight(next);
    storePanelHeight(MEDIA_PANEL_HEIGHT_KEY, next);
  };
  const mediaPanelStyle = { height: `${mediaPanelHeight}px` };
  const mediaToggle = (
    <TextToggle
      options={['Map', 'Video']}
      value={showVideo}
      disabled={isDemoDevice}
      setValue={(value) => {
        setShowVideo(value);
        if (value) setStopLiveAudioSignal((prev) => prev + 1);
      }}
    />
  );

  return (
    <div
      className={`d-flex flex-column gap-2 overflow-x-hidden${isMediaOnlySettings ? ' min-h-75vh' : ''}`}
    >
      {/* Top */}
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column">
          {!showVideo && (
            <div className="w-100 overflow-hidden rounded-3" style={mediaPanelStyle}>
              <DeviceMap
                mode="modal"
                disableFilter={true}
                devicesOverride={displayDevice ? [displayDevice] : []}
                selectedDeviceOverride={displayDevice}
              />
            </div>
          )}

          {showVideo &&
            !isDemoDevice &&
            (device.device_type?.toLowerCase() === 'overwatch vms' || device?.camera) &&
            device.id && (
              <div className="w-100 overflow-hidden rounded-3" style={mediaPanelStyle}>
                <VideoPlayer src={videoFeedUrl} ariaLabel={videoPlayerLabel} />
              </div>
            )}

          <DragResizeHandle
            ariaLabel="Resize the map and video panel"
            value={mediaPanelHeight}
            onChange={resizeMediaPanel}
            onReset={() => resizeMediaPanel(mediaPanelDefaultPx)}
          />

          {(device.device_type?.toLowerCase() === 'overwatch vms' || device.camera) && (
            <div className="mt-2">
              {isDemoDevice ? (
                <BaseTooltip
                  id="disabled-video-toggle-tooltip"
                  text="No VMS devices are linked to the selected Siren/Giant Voice device"
                  placement="top"
                >
                  <span className="d-block">{mediaToggle}</span>
                </BaseTooltip>
              ) : (
                mediaToggle
              )}
            </div>
          )}
        </div>

        {perms.isMaintenanceOperator() &&
          layout[device.device_type.toLowerCase()] &&
          device.device_type !== 'Manual Road Closure' && (
            <Button
              variant={maintenanceActive ? 'warning' : 'primary'}
              onClick={onEnableMaintenanceMode}
            >
              {maintenanceActive ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
            </Button>
          )}

        {createEditPermission() && layout[displayDevice.device_type.toLowerCase()] && (
          <DeviceControls
            device={displayDevice}
            on={deviceControlListener}
            sending={sending}
            audioOverride={audioOverride}
            stopLiveAudioSignal={stopLiveAudioSignal}
          />
        )}

        {/* Audio manager access for camera-enabled sirens — always accessible regardless of device status */}
        {device?.camera &&
          (perms.testPermission([], ['ViewSirenAudioFiles']) ||
            perms.testPermission([], ['CreateSirenAudioFiles']) ||
            perms.testPermission([], ['Edit-DeleteSirenAudioFiles'])) && (
            <Button
              variant="primary"
              className="d-flex align-items-center gap-2"
              onClick={() => setAudioManager(true)}
            >
              <PiPencilLineFill />
              Manage Audio Files
            </Button>
          )}
      </div>

      {/* Bottom: device history panel */}
      {layout[device.device_type.toLowerCase()] && (
        <div className="d-flex flex-column">
          <Button
            type="button"
            variant="primary"
            className="d-flex align-items-center justify-content-center gap-2"
            onClick={() => setHistoryOpen((prev) => !prev)}
            aria-expanded={historyOpen}
            aria-controls="standard-device-history"
          >
            <span>Device History</span>
            <FaChevronDown
              size={12}
              style={{
                transform: historyOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
            />
          </Button>

          <Collapse in={historyOpen}>
            <div id="standard-device-history" className="pt-2">
              <DeviceHistory
                title={'Device History'}
                subTitle={device.alias}
                data={device?.data}
                deviceType={device.device_type}
                loading={historyLoading}
                hasMore={historyHasMore}
                onLoadMore={loadMoreHistory}
                error={historyError}
              />
            </div>
          </Collapse>
        </div>
      )}

      {/* Audio Manager */}
      <BaseModal
        contentClassName="overflow-auto"
        contentStyle={{ height: '75dvh' }}
        show={perms.testPermission([], ['ViewSirenAudioFiles']) && device?.camera && audioManager}
        onHide={() => setAudioManager(false)}
        closeButton
        size="lg"
        centered
        stackLevel={2}
      >
        <AudioManager
          deviceIds={audioManagerDeviceIds}
          onClose={() => setAudioManager(false)}
          onPlayTemp={onSetTempAudio}
        />
      </BaseModal>
    </div>
  );
}
